import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { PlatformApi } from '../../api/resources';
import type {
  Branch,
  BranchAccessStatus,
  BranchSubscription,
  Company,
  LicensePeriod,
  Payment,
  PaymentStatus,
  PlatformDashboardData,
} from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const periodLabels: Record<LicensePeriod, string> = {
  MENSUAL: 'Mensual',
  TRIMESTRAL: 'Trimestral',
  SEMESTRAL: 'Semestral',
  ANUAL: 'Anual',
};

const statusLabels: Record<BranchAccessStatus, string> = {
  PENDIENTE: 'Pendiente',
  ACTIVA: 'Activa',
  VENCIDA: 'Vencida',
  SUSPENDIDA: 'Suspendida',
  INACTIVA: 'Inactiva',
};

const emptyCompany = {
  commercialName: '',
  legalName: '',
  taxId: '',
  country: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
};

function dateText(value?: string | null) {
  return value ? new Date(value).toLocaleDateString('es-CO', { timeZone: 'UTC' }) : '—';
}

function moneyText(value?: string | number | null) {
  return value === undefined || value === null ? '—' : new Intl.NumberFormat('es-CO').format(Number(value));
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

function statusClass(status?: BranchAccessStatus) {
  if (status === 'ACTIVA') return 'pill-active';
  if (status === 'PENDIENTE') return 'pill-warning';
  return 'pill-inactive';
}

export function PlatformAdmin() {
  const { user, logout } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'resumen' | 'empresas' | 'sucursales'>('resumen');
  const [branchCompanyFilter, setBranchCompanyFilter] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<PlatformDashboardData | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [branchEditForm, setBranchEditForm] = useState({
    name: '',
    commercialName: '',
    address: '',
    city: '',
    department: '',
    country: '',
    phone: '',
    email: '',
    managerName: '',
    managerTitle: '',
    managerPhone: '',
    managerEmail: '',
    observations: '',
    maxUsers: '5',
  });
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [adminForms, setAdminForms] = useState<Record<string, { username: string; fullName: string; email: string }>>({});
  const [modalPassword, setModalPassword] = useState<{ username: string; value: string } | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<{ username: string; value: string } | null>(null);
  const [paymentSubscription, setPaymentSubscription] = useState<string | null>(null);
  const [licenseForm, setLicenseForm] = useState({ period: 'MENSUAL' as LicensePeriod, startDate: new Date().toISOString().slice(0, 10), contractedAmount: '0' });
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: new Date().toISOString().slice(0, 10),
    amountPaid: '',
    paymentMethod: 'Transferencia bancaria',
    status: 'PAGADO' as PaymentStatus,
    observations: '',
  });
  const [branchForm, setBranchForm] = useState({
    companyId: '',
    name: '',
    commercialName: '',
    address: '',
    city: '',
    department: '',
    country: '',
    phone: '',
    email: '',
    managerName: '',
    managerTitle: '',
    managerPhone: '',
    managerEmail: '',
    maxUsers: '5',
    adminUsername: '',
    adminEmail: '',
    adminFullName: '',
    adminPassword: '',
    period: 'ANUAL' as LicensePeriod,
    startDate: new Date().toISOString().slice(0, 10),
    contractedAmount: '0',
  });

  async function loadAll() {
    setLoading(true);
    try {
      const [summary, companyResult, branchResult] = await Promise.all([
        PlatformApi.dashboard(),
        PlatformApi.companies(),
        PlatformApi.branches(),
      ]);
      setDashboard(summary);
      setCompanies(companyResult.companies);
      setBranches(branchResult.branches);
      if (selectedBranch) {
        const detail = await PlatformApi.branch(selectedBranch.id);
        setSelectedBranch(detail.branch);
        setLicenseForm((current) => ({
          ...current,
          period: detail.branch.subscriptions?.[0]?.period ?? current.period,
        }));
      }
    } catch (error) {
      notify(errorMessage(error, 'No fue posible cargar los datos de la plataforma.'), 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // loadAll intentionally runs once at page entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!editingBranch) return undefined;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !saving) setEditingBranch(null);
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [editingBranch, saving]);

  async function refreshBranch(id: string) {
    const [detail, branchResult, summary] = await Promise.all([
      PlatformApi.branch(id),
      PlatformApi.branches(),
      PlatformApi.dashboard(),
    ]);
    setSelectedBranch(detail.branch);
    setLicenseForm((current) => ({
      ...current,
      period: detail.branch.subscriptions?.[0]?.period ?? 'MENSUAL',
    }));
    setBranches(branchResult.branches);
    setDashboard(summary);
  }

  async function submitCompany(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const companyData = Object.fromEntries(
        Object.entries(companyForm).map(([key, value]) => [key, value.trim() || (editingCompanyId ? null : undefined)]),
      );
      if (editingCompanyId) {
        await PlatformApi.updateCompany(editingCompanyId, companyData);
        setEditingCompanyId(null);
      } else {
        await PlatformApi.createCompany(companyData);
      }
      setCompanyForm(emptyCompany);
      setShowCompanyForm(false);
      notify(editingCompanyId ? 'Empresa actualizada correctamente.' : 'Empresa creada correctamente.', 'success');
      await loadAll();
    } catch (error) {
      notify(errorMessage(error, 'No fue posible crear la empresa.'), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function submitBranch(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const { branch, temporaryPassword: issuedPassword } = await PlatformApi.createBranch({
        companyId: branchForm.companyId,
        name: branchForm.name,
        commercialName: branchForm.commercialName || undefined,
        address: branchForm.address || undefined,
        city: branchForm.city || undefined,
        department: branchForm.department || undefined,
        country: branchForm.country || undefined,
        phone: branchForm.phone || undefined,
        email: branchForm.email || undefined,
        managerName: branchForm.managerName || undefined,
        managerTitle: branchForm.managerTitle || undefined,
        managerPhone: branchForm.managerPhone || undefined,
        managerEmail: branchForm.managerEmail || undefined,
        maxUsers: Number(branchForm.maxUsers),
        adminUsername: branchForm.adminUsername,
        adminEmail: branchForm.adminEmail,
        adminFullName: branchForm.adminFullName,
        adminPassword: branchForm.adminPassword || undefined,
        license: {
          period: branchForm.period,
          startDate: branchForm.startDate,
          contractedAmount: Number(branchForm.contractedAmount),
        },
      });
      setTemporaryPassword({ username: branchForm.adminUsername, value: issuedPassword });
      setShowBranchForm(false);
      setBranchForm((current) => ({ ...current, name: '', adminUsername: '', adminEmail: '', adminFullName: '', adminPassword: '' }));
      setTab('sucursales');
      notify('Sucursal creada. Comunica la contraseña temporal por un canal seguro.', 'success');
      await loadAll();
      const detail = await PlatformApi.branch(branch.id);
      setSelectedBranch(detail.branch);
      setLicenseForm((current) => ({
        ...current,
        period: detail.branch.subscriptions?.[0]?.period ?? 'MENSUAL',
      }));
    } catch (error) {
      notify(errorMessage(error, 'No fue posible crear la sucursal.'), 'error');
    } finally {
      setSaving(false);
    }
  }

  function openBranchEditor(branch: Branch) {
    setAdminForms({});
    setModalPassword(null);
    void PlatformApi.branch(branch.id).then(({ branch: detail }) => {
      setAdminForms(Object.fromEntries(
        (detail.users ?? [])
          .filter((item) => item.role === 'ADMIN')
          .map((item) => [item.id, { username: item.username, fullName: item.fullName, email: item.email }]),
      ));
      setEditingBranch((current) => (current?.id === detail.id ? { ...current, users: detail.users } : current));
    }).catch(() => notify('No fue posible cargar los administradores de la sucursal.', 'error'));
    setBranchEditForm({
      name: branch.name,
      commercialName: branch.commercialName ?? '',
      address: branch.address ?? '',
      city: branch.city ?? '',
      department: branch.department ?? '',
      country: branch.country ?? '',
      phone: branch.phone ?? '',
      email: branch.email ?? '',
      managerName: branch.managerName ?? '',
      managerTitle: branch.managerTitle ?? '',
      managerPhone: branch.managerPhone ?? '',
      managerEmail: branch.managerEmail ?? '',
      observations: branch.observations ?? '',
      maxUsers: String(branch.maxUsers ?? 5),
    });
    setEditingBranch(branch);
  }

  async function submitBranchEdit(event: FormEvent) {
    event.preventDefault();
    if (!editingBranch) return;
    setSaving(true);
    try {
      const { maxUsers, ...textFields } = branchEditForm;
      const data: Record<string, unknown> = Object.fromEntries(
        Object.entries(textFields).map(([key, value]) => [key, value.trim()]),
      );
      data.maxUsers = Number(maxUsers);
      await PlatformApi.updateBranch(editingBranch.id, data);
      for (const admin of (editingBranch.users ?? []).filter((item) => item.role === 'ADMIN')) {
        const form = adminForms[admin.id];
        if (form && (form.username !== admin.username || form.fullName !== admin.fullName || form.email !== admin.email)) {
          await PlatformApi.updateBranchAdmin(editingBranch.id, admin.id, {
            username: form.username.trim(),
            fullName: form.fullName.trim(),
            email: form.email.trim(),
          });
        }
      }
      await refreshBranch(editingBranch.id);
      setEditingBranch(null);
      notify('Datos de la sucursal actualizados correctamente.', 'success');
    } catch (error) {
      notify(errorMessage(error, 'No fue posible editar la sucursal.'), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function toggleBranchAccess(branch: Branch) {
    try {
      await PlatformApi.setBranchAccess(branch.id, !branch.manualAccessEnabled);
      await refreshBranch(branch.id);
      notify('Control manual de acceso actualizado.', 'success');
    } catch (error) {
      notify(errorMessage(error, 'No fue posible actualizar el acceso.'), 'error');
    }
  }

  async function toggleBranchActive(branch: Branch) {
    try {
      await PlatformApi.setBranchActive(branch.id, !branch.isActive);
      await refreshBranch(branch.id);
      notify('Estado de la sucursal actualizado.', 'success');
    } catch (error) {
      notify(errorMessage(error, 'No fue posible actualizar la sucursal.'), 'error');
    }
  }

  async function toggleCompanyActive(company: Company) {
    try {
      await PlatformApi.setCompanyActive(company.id, !company.isActive);
      await loadAll();
      notify('Estado de la empresa actualizado.', 'success');
    } catch (error) {
      notify(errorMessage(error, 'No fue posible actualizar la empresa.'), 'error');
    }
  }

  async function submitLicense(event: FormEvent) {
    event.preventDefault();
    if (!selectedBranch) return;
    setSaving(true);
    try {
      await PlatformApi.renewSubscription(selectedBranch.id, {
        ...licenseForm,
        contractedAmount: Number(licenseForm.contractedAmount),
      });
      await refreshBranch(selectedBranch.id);
      notify('Licencia registrada. Para valores mayores a cero, registra el pago para habilitar el acceso.', 'success');
    } catch (error) {
      notify(errorMessage(error, 'No fue posible registrar la licencia.'), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function submitPayment(event: FormEvent, subscription: BranchSubscription) {
    event.preventDefault();
    if (!selectedBranch) return;
    setSaving(true);
    try {
      await PlatformApi.registerPayment(selectedBranch.id, subscription.id, {
        ...paymentForm,
        modality: subscription.period,
        amountPaid: Number(paymentForm.amountPaid),
      });
      setPaymentSubscription(null);
      setPaymentForm((current) => ({ ...current, amountPaid: '', observations: '' }));
      await refreshBranch(selectedBranch.id);
      notify('Pago registrado en el historial.', 'success');
    } catch (error) {
      notify(errorMessage(error, 'No fue posible registrar el pago.'), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function changePaymentStatus(payment: Payment, status: PaymentStatus) {
    if (!selectedBranch) return;
    try {
      await PlatformApi.updatePayment(payment.id, { status });
      await refreshBranch(selectedBranch.id);
      notify('Estado del pago actualizado.', 'success');
    } catch (error) {
      notify(errorMessage(error, 'No fue posible actualizar el pago.'), 'error');
    }
  }

  async function handleLogout() {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch {
      notify('No fue posible cerrar la sesión.', 'error');
    }
  }

  return (
    <div className="platform-shell">
      <header className="platform-header">
        <div className="sidebar-brand">
          <span className="brand-mark" aria-hidden="true">FS</span>
          <span className="brand-lockup">FLOWERSOFT<small>Administración principal</small></span>
        </div>
        <div className="topbar-user">
          <span>{user?.fullName}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleLogout}>Cerrar sesión</button>
        </div>
      </header>
      <main className="page-content platform-content">
        <div className="flex-between">
          <div>
            <h1>Administración de plataforma</h1>
            <p>Empresas, sucursales, licencias y pagos.</p>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => void loadAll()}>Actualizar</button>
        </div>

        <nav className="platform-tabs" aria-label="Secciones de administración">
          {(['resumen', 'empresas', 'sucursales'] as const).map((value) => (
            <button
              key={value}
              type="button"
              className={`btn ${tab === value ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { if (value === 'sucursales') setBranchCompanyFilter(null); setTab(value); }}
            >
              {value[0].toUpperCase() + value.slice(1)}
            </button>
          ))}
        </nav>

        {temporaryPassword && (
          <div className="alert alert-info" role="status">
            Clave temporal de <strong>{temporaryPassword.username}</strong>: <code>{temporaryPassword.value}</code>.
            Comunícala de forma segura; se solicitará cambio al iniciar sesión.
            <button type="button" className="btn-link" onClick={() => setTemporaryPassword(null)}>Cerrar aviso</button>
          </div>
        )}

        {loading ? <div className="loading-block">Cargando plataforma...</div> : null}

        {!loading && tab === 'resumen' && dashboard && (
          <>
            <section className="platform-stat-grid" aria-label="Indicadores generales">
              <Stat label="Empresas" value={dashboard.companyCount} />
              <Stat label="Sucursales" value={dashboard.branchCount} />
              <Stat label="Activas" value={dashboard.activeBranchCount} />
              <Stat label="Vencidas" value={dashboard.expiredBranchCount} />
              <Stat label="Suspendidas" value={dashboard.suspendedBranchCount} />
              <Stat label="Por vencer (30 días)" value={dashboard.upcomingExpiryCount} />
              <Stat label="Pagos pendientes" value={dashboard.pendingPaymentCount} detail={moneyText(dashboard.pendingPaymentAmount)} />
              <Stat label="Pagos recibidos" value={moneyText(dashboard.receivedPaymentAmount)} />
            </section>

            <section className="card" aria-labelledby="platform-companies-heading" style={{ marginTop: '1.25rem' }}>
              <div className="flex-between">
                <div>
                  <h2 id="platform-companies-heading">Empresas registradas</h2>
                  <p>Datos de contacto, identificación y estado de sus sucursales.</p>
                </div>
                <button type="button" className="btn btn-secondary" onClick={() => setTab('empresas')}>
                  Gestionar empresas
                </button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Empresa</th>
                      <th>Identificación</th>
                      <th>Contacto</th>
                      <th>Sucursales</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {companies.map((company) => {
                      const companyBranches = branches.filter((branch) => branch.companyId === company.id);
                      const activeBranches = companyBranches.filter((branch) => branch.accessStatus === 'ACTIVA').length;
                      return (
                        <tr key={company.id}>
                          <td>
                            <strong>{company.commercialName}</strong>
                            <br />{company.legalName || 'Sin razón social'}
                          </td>
                          <td>{company.taxId || '—'}<br />{company.country || 'País no especificado'}</td>
                          <td>
                            {company.contactName || 'Sin contacto asignado'}
                            <br />{company.contactEmail || '—'}
                            {company.contactPhone ? <><br />{company.contactPhone}</> : null}
                          </td>
                          <td>{companyBranches.length} total · {activeBranches} activas</td>
                          <td>
                            <span className={`pill ${company.isActive ? 'pill-active' : 'pill-inactive'}`}>
                              {company.isActive ? 'Activa' : 'Inactiva'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {companies.length === 0 && (
                      <tr><td colSpan={5}>No hay empresas registradas.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {!loading && tab === 'empresas' && (
          <section className="card">
            <div className="flex-between">
              <h2>Empresas</h2>
              <button type="button" className="btn btn-primary" onClick={() => setShowCompanyForm((open) => !open)}>
                {showCompanyForm ? 'Cerrar formulario' : '+ Nueva empresa'}
              </button>
            </div>
            {showCompanyForm && (
              <form className="platform-form" onSubmit={submitCompany}>
                <div className="form-grid">
                  <TextField label="Nombre comercial" value={companyForm.commercialName} onChange={(value) => setCompanyForm({ ...companyForm, commercialName: value })} required />
                  <TextField label="Razón social" value={companyForm.legalName} onChange={(value) => setCompanyForm({ ...companyForm, legalName: value })} />
                  <TextField label="NIT" value={companyForm.taxId} onChange={(value) => setCompanyForm({ ...companyForm, taxId: value })} />
                  <TextField label="País" value={companyForm.country} onChange={(value) => setCompanyForm({ ...companyForm, country: value })} />
                  <TextField label="Contacto" value={companyForm.contactName} onChange={(value) => setCompanyForm({ ...companyForm, contactName: value })} />
                  <TextField label="Correo" type="email" value={companyForm.contactEmail} onChange={(value) => setCompanyForm({ ...companyForm, contactEmail: value })} />
                  <TextField label="Teléfono" value={companyForm.contactPhone} onChange={(value) => setCompanyForm({ ...companyForm, contactPhone: value })} />
                </div>
                <div className="actions-row">
                  <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Guardando...' : editingCompanyId ? 'Guardar cambios' : 'Crear empresa'}</button>
                  {editingCompanyId && <button className="btn btn-secondary" type="button" onClick={() => { setEditingCompanyId(null); setCompanyForm(emptyCompany); setShowCompanyForm(false); }}>Cancelar</button>}
                </div>
              </form>
            )}
            <div className="table-wrap">
              <table>
                <thead><tr><th>Nombre comercial</th><th>Razón social</th><th>NIT</th><th>Sucursales</th><th>Estado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {companies.map((company) => (
                    <tr key={company.id}>
                      <td>{company.commercialName}</td><td>{company.legalName || '—'}</td><td>{company.taxId || '—'}</td>
                      <td>{company._count?.branches ?? 0}</td>
                      <td><span className={`pill ${company.isActive ? 'pill-active' : 'pill-inactive'}`}>{company.isActive ? 'Activa' : 'Inactiva'}</span></td>
                      <td><div className="actions-row">
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => {
                          setEditingCompanyId(company.id);
                          setCompanyForm({
                            commercialName: company.commercialName,
                            legalName: company.legalName ?? '',
                            taxId: company.taxId ?? '',
                            country: company.country ?? '',
                            contactName: company.contactName ?? '',
                            contactEmail: company.contactEmail ?? '',
                            contactPhone: company.contactPhone ?? '',
                          });
                          setShowCompanyForm(true);
                        }}>Editar</button>
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => void toggleCompanyActive(company)}>
                          {company.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                        <button className="btn btn-secondary btn-sm" type="button" onClick={() => { setBranchCompanyFilter(company.id); setTab('sucursales'); }}>
                          Ver sucursales
                        </button>
                      </div></td>
                    </tr>
                  ))}
                  {!companies.length && <tr><td colSpan={6}>No hay empresas registradas.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {!loading && tab === 'sucursales' && (
          <section className="platform-branch-layout">
            <div className="card">
              <div className="flex-between">
                <h2>Sucursales</h2>
                <button type="button" className="btn btn-primary" onClick={() => setShowBranchForm((open) => !open)} disabled={!companies.some((item) => item.isActive)}>
                  {showBranchForm ? 'Cerrar formulario' : '+ Nueva sucursal'}
                </button>
              </div>
              {showBranchForm && (
                <form className="platform-form" onSubmit={submitBranch}>
                  <h3>Empresa y sucursal</h3>
                  <div className="form-grid">
                    <div className="field"><label htmlFor="branch-company">Empresa</label>
                      <select id="branch-company" required value={branchForm.companyId} onChange={(event) => setBranchForm({ ...branchForm, companyId: event.target.value })}>
                        <option value="">Selecciona una empresa</option>
                        {companies.filter((company) => company.isActive).map((company) => <option key={company.id} value={company.id}>{company.commercialName}</option>)}
                      </select>
                    </div>
                    <TextField label="Nombre de sucursal" value={branchForm.name} onChange={(value) => setBranchForm({ ...branchForm, name: value })} required />
                    <TextField label="Nombre comercial" value={branchForm.commercialName} onChange={(value) => setBranchForm({ ...branchForm, commercialName: value })} />
                    <TextField label="Dirección" value={branchForm.address} onChange={(value) => setBranchForm({ ...branchForm, address: value })} />
                    <TextField label="Ciudad" value={branchForm.city} onChange={(value) => setBranchForm({ ...branchForm, city: value })} />
                    <TextField label="Departamento" value={branchForm.department} onChange={(value) => setBranchForm({ ...branchForm, department: value })} />
                    <TextField label="País" value={branchForm.country} onChange={(value) => setBranchForm({ ...branchForm, country: value })} />
                    <TextField label="Teléfono" value={branchForm.phone} onChange={(value) => setBranchForm({ ...branchForm, phone: value })} />
                    <TextField label="Correo de sucursal" type="email" value={branchForm.email} onChange={(value) => setBranchForm({ ...branchForm, email: value })} />
                  </div>
                  <h3>Responsable y administrador de sucursal</h3>
                  <div className="form-grid">
                    <TextField label="Responsable" value={branchForm.managerName} onChange={(value) => setBranchForm({ ...branchForm, managerName: value })} />
                    <TextField label="Cargo" value={branchForm.managerTitle} onChange={(value) => setBranchForm({ ...branchForm, managerTitle: value })} />
                    <TextField label="Teléfono del responsable" value={branchForm.managerPhone} onChange={(value) => setBranchForm({ ...branchForm, managerPhone: value })} />
                    <TextField label="Correo del responsable" type="email" value={branchForm.managerEmail} onChange={(value) => setBranchForm({ ...branchForm, managerEmail: value })} />
                    <TextField label="Usuario administrador" value={branchForm.adminUsername} onChange={(value) => setBranchForm({ ...branchForm, adminUsername: value })} required />
                    <TextField label="Nombre administrador" value={branchForm.adminFullName} onChange={(value) => setBranchForm({ ...branchForm, adminFullName: value })} required />
                    <TextField label="Correo administrador" type="email" value={branchForm.adminEmail} onChange={(value) => setBranchForm({ ...branchForm, adminEmail: value })} required />
                    <TextField label="Contraseña (opcional)" type="password" value={branchForm.adminPassword} onChange={(value) => setBranchForm({ ...branchForm, adminPassword: value })} />
                  </div>
                  <h3>Licencia inicial</h3>
                  <div className="form-grid">
                    <PeriodField value={branchForm.period} onChange={(period) => setBranchForm({ ...branchForm, period })} />
                    <TextField label="Inicio" type="date" value={branchForm.startDate} onChange={(value) => setBranchForm({ ...branchForm, startDate: value })} required />
                    <TextField label="Valor contratado" type="number" value={branchForm.contractedAmount} onChange={(value) => setBranchForm({ ...branchForm, contractedAmount: value })} required />
                    <TextField label="Máximo de usuarios activos (incluye al administrador)" type="number" value={branchForm.maxUsers} onChange={(value) => setBranchForm({ ...branchForm, maxUsers: value })} required />
                  </div>
                  <p className="hint">La fecha de vencimiento se calcula automáticamente. Si el valor es mayor que cero, registra los pagos para habilitar la sucursal.</p>
                  <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Creando...' : 'Crear sucursal y cuenta'}</button>
                </form>
              )}
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Empresa / sucursal</th><th>Licencia</th><th>Estado de acceso</th><th>Usuarios</th><th>Acciones</th></tr></thead>
                  <tbody>
                    {branches.filter((branch) => !branchCompanyFilter || branch.company.id === branchCompanyFilter).map((branch) => (
                      <tr key={branch.id} className={selectedBranch?.id === branch.id ? 'selected-row' : ''}>
                        <td><strong>{branch.company.commercialName}</strong><br />{branch.name}</td>
                        <td>{branch.currentSubscription ? `${periodLabels[branch.currentSubscription.period]} · ${dateText(branch.currentSubscription.endDate)}` : 'Sin licencia'}</td>
                        <td><span className={`pill ${statusClass(branch.accessStatus)}`}>{statusLabels[branch.accessStatus ?? 'PENDIENTE']}</span></td>
                        <td>{branch._count?.users ?? 0}</td>
                        <td><div className="actions-row platform-icon-actions">
                          <IconAction
                            label="Administrar sucursal"
                            icon="manage"
                            onClick={() => void refreshBranch(branch.id)}
                          />
                          <IconAction
                            label={branch.manualAccessEnabled ? 'Suspender acceso' : 'Habilitar acceso manualmente'}
                            icon={branch.manualAccessEnabled ? 'lock' : 'unlock'}
                            onClick={() => void toggleBranchAccess(branch)}
                          />
                          <IconAction
                            label={branch.isActive ? 'Desactivar sucursal' : 'Activar sucursal'}
                            icon={branch.isActive ? 'deactivate' : 'activate'}
                            onClick={() => void toggleBranchActive(branch)}
                          />
                        </div></td>
                      </tr>
                    ))}
                    {!branches.length && <tr><td colSpan={5}>No hay sucursales registradas.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {selectedBranch && (
              <BranchPanel
                branch={selectedBranch}
                saving={saving}
                licenseForm={licenseForm}
                paymentSubscription={paymentSubscription}
                paymentForm={paymentForm}
                onLicenseChange={setLicenseForm}
                onPaymentChange={setPaymentForm}
                onRenew={submitLicense}
                onTogglePayment={setPaymentSubscription}
                onPaymentSubmit={submitPayment}
                onPaymentStatus={changePaymentStatus}
                onEdit={() => openBranchEditor(selectedBranch)}
              />
            )}
          </section>
        )}

        {editingBranch && (
          <div
            className="platform-modal-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !saving) setEditingBranch(null);
            }}
          >
            <section className="platform-modal" role="dialog" aria-modal="true" aria-labelledby="edit-branch-title">
              <div className="platform-modal-header">
                <div>
                  <h2 id="edit-branch-title">Editar sucursal</h2>
                  <p>{editingBranch.company.commercialName}</p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setEditingBranch(null)}
                  disabled={saving}
                  aria-label="Cerrar edición"
                >
                  Cerrar
                </button>
              </div>
              <form className="platform-form" onSubmit={submitBranchEdit}>
                <h3>Datos de la sucursal</h3>
                <div className="form-grid">
                  <TextField id="edit-branch-name" label="Nombre de sucursal" value={branchEditForm.name} onChange={(value) => setBranchEditForm({ ...branchEditForm, name: value })} required />
                  <TextField id="edit-branch-commercial-name" label="Nombre comercial" value={branchEditForm.commercialName} onChange={(value) => setBranchEditForm({ ...branchEditForm, commercialName: value })} />
                  <TextField id="edit-branch-address" label="Dirección" value={branchEditForm.address} onChange={(value) => setBranchEditForm({ ...branchEditForm, address: value })} />
                  <TextField id="edit-branch-city" label="Ciudad" value={branchEditForm.city} onChange={(value) => setBranchEditForm({ ...branchEditForm, city: value })} />
                  <TextField id="edit-branch-department" label="Departamento" value={branchEditForm.department} onChange={(value) => setBranchEditForm({ ...branchEditForm, department: value })} />
                  <TextField id="edit-branch-country" label="País" value={branchEditForm.country} onChange={(value) => setBranchEditForm({ ...branchEditForm, country: value })} />
                  <TextField id="edit-branch-phone" label="Teléfono" value={branchEditForm.phone} onChange={(value) => setBranchEditForm({ ...branchEditForm, phone: value })} />
                  <TextField id="edit-branch-email" label="Correo de sucursal" type="email" value={branchEditForm.email} onChange={(value) => setBranchEditForm({ ...branchEditForm, email: value })} />
                </div>
                <h3>Responsable de la sucursal</h3>
                <div className="form-grid">
                  <TextField id="edit-branch-manager" label="Responsable" value={branchEditForm.managerName} onChange={(value) => setBranchEditForm({ ...branchEditForm, managerName: value })} />
                  <TextField id="edit-branch-manager-title" label="Cargo" value={branchEditForm.managerTitle} onChange={(value) => setBranchEditForm({ ...branchEditForm, managerTitle: value })} />
                  <TextField id="edit-branch-manager-phone" label="Teléfono del responsable" value={branchEditForm.managerPhone} onChange={(value) => setBranchEditForm({ ...branchEditForm, managerPhone: value })} />
                  <TextField id="edit-branch-manager-email" label="Correo del responsable" type="email" value={branchEditForm.managerEmail} onChange={(value) => setBranchEditForm({ ...branchEditForm, managerEmail: value })} />
                </div>
                <TextField id="edit-branch-observations" label="Observaciones" value={branchEditForm.observations} onChange={(value) => setBranchEditForm({ ...branchEditForm, observations: value })} />
                <h3>Usuarios de la sucursal</h3>
                <div className="form-grid">
                  <TextField id="edit-branch-max-users" label="Máximo de usuarios activos (incluye al administrador)" type="number" value={branchEditForm.maxUsers} onChange={(value) => setBranchEditForm({ ...branchEditForm, maxUsers: value })} required />
                </div>
                <p className="hint">
                  Activos: {(editingBranch.users ?? []).filter((item) => item.isActive).length} de {branchEditForm.maxUsers || '—'}
                </p>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Usuario</th><th>Nombre</th><th>Rol</th><th>Estado</th><th></th></tr></thead>
                    <tbody>
                      {(editingBranch.users ?? []).map((item) => (
                        <tr key={item.id}>
                          <td>{item.username}</td>
                          <td>{item.fullName}</td>
                          <td>{item.role === 'ADMIN' ? 'Administrador' : 'Empleado'}</td>
                          <td>{item.isActive ? 'Activo' : 'Inactivo'}</td>
                          <td>
                            <button
                              type="button"
                              className={`btn btn-sm ${item.isActive ? 'btn-danger' : 'btn-secondary'}`}
                              disabled={saving}
                              onClick={async () => {
                                try {
                                  await PlatformApi.setBranchUserActive(editingBranch.id, item.id, !item.isActive);
                                  const { branch: detail } = await PlatformApi.branch(editingBranch.id);
                                  setEditingBranch((current) => (current ? { ...current, users: detail.users } : current));
                                  notify(item.isActive ? 'Usuario deshabilitado.' : 'Usuario habilitado.', 'success');
                                } catch (error) {
                                  notify(errorMessage(error, 'No fue posible actualizar el usuario.'), 'error');
                                }
                              }}
                            >
                              {item.isActive ? 'Deshabilitar' : 'Habilitar'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(editingBranch.users ?? []).filter((item) => item.role === 'ADMIN').map((admin) => {
                  const form = adminForms[admin.id] ?? { username: admin.username, fullName: admin.fullName, email: admin.email };
                  const update = (patch: Partial<typeof form>) => setAdminForms({ ...adminForms, [admin.id]: { ...form, ...patch } });
                  return (
                    <div key={admin.id}>
                      <h3>Administrador de la sucursal</h3>
                      <div className="form-grid">
                        <TextField id={`edit-admin-username-${admin.id}`} label="Usuario" value={form.username} onChange={(value) => update({ username: value })} required />
                        <TextField id={`edit-admin-name-${admin.id}`} label="Nombre" value={form.fullName} onChange={(value) => update({ fullName: value })} required />
                        <TextField id={`edit-admin-email-${admin.id}`} label="Correo" type="email" value={form.email} onChange={(value) => update({ email: value })} required />
                      </div>
                      <div className="actions-row">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={saving}
                          onClick={async () => {
                            if (!window.confirm(`¿Restablecer la contraseña de "${admin.username}"? Se generará una clave temporal.`)) return;
                            try {
                              const { temporaryPassword: value } = await PlatformApi.resetBranchAdminPassword(editingBranch.id, admin.id);
                              setModalPassword({ username: admin.username, value });
                            } catch (error) {
                              notify(errorMessage(error, 'No fue posible restablecer la contraseña.'), 'error');
                            }
                          }}
                        >
                          Restablecer contraseña
                        </button>
                      </div>
                      {modalPassword?.username === admin.username && (
                        <div className="alert alert-info" role="status">
                          Clave temporal: <code>{modalPassword.value}</code>. Comunícala de forma segura; deberá cambiarla al ingresar.
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="actions-row platform-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setEditingBranch(null)} disabled={saving}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Guardar cambios'}</button>
                </div>
              </form>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, detail }: { label: string; value: string | number; detail?: string }) {
  return <article className="card platform-stat"><span>{label}</span><strong>{value}</strong>{detail && <small>{detail}</small>}</article>;
}

function TextField({
  label,
  value,
  onChange,
  id: suppliedId,
  type = 'text',
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  type?: string;
  required?: boolean;
}) {
  const id = suppliedId ?? `platform-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return <div className="field"><label htmlFor={id}>{label}</label><input id={id} type={type} value={value} required={required} onChange={(event) => onChange(event.target.value)} /></div>;
}

function PeriodField({ value, onChange }: { value: LicensePeriod; onChange: (period: LicensePeriod) => void }) {
  return (
    <div className="field"><label htmlFor="license-period">Período</label>
      <select id="license-period" value={value} onChange={(event) => onChange(event.target.value as LicensePeriod)}>
        {Object.entries(periodLabels).map(([period, label]) => <option key={period} value={period}>{label}</option>)}
      </select>
    </div>
  );
}

type PlatformActionIcon = 'manage' | 'lock' | 'unlock' | 'deactivate' | 'activate';

function IconAction({ label, icon, onClick }: { label: string; icon: PlatformActionIcon; onClick: () => void }) {
  return (
    <button
      className={`platform-icon-action platform-icon-action-${icon}`}
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        {icon === 'manage' && (
          <>
            <path d="M4 6h16M4 12h16M4 18h16" />
            <circle cx="8" cy="6" r="2" />
            <circle cx="15" cy="12" r="2" />
            <circle cx="10" cy="18" r="2" />
          </>
        )}
        {icon === 'lock' && (
          <>
            <rect x="4" y="10" width="16" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3" />
          </>
        )}
        {icon === 'unlock' && (
          <>
            <rect x="4" y="10" width="16" height="11" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 7.5-2M12 14v3" />
          </>
        )}
        {icon === 'deactivate' && (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="M8 12h8" />
          </>
        )}
        {icon === 'activate' && (
          <>
            <circle cx="12" cy="12" r="9" />
            <path d="m8 12 2.5 2.5L16 9" />
          </>
        )}
      </svg>
    </button>
  );
}

function BranchPanel({
  branch,
  saving,
  licenseForm,
  paymentSubscription,
  paymentForm,
  onLicenseChange,
  onPaymentChange,
  onRenew,
  onTogglePayment,
  onPaymentSubmit,
  onPaymentStatus,
  onEdit,
}: {
  branch: Branch;
  saving: boolean;
  licenseForm: { period: LicensePeriod; startDate: string; contractedAmount: string };
  paymentSubscription: string | null;
  paymentForm: { paymentDate: string; amountPaid: string; paymentMethod: string; status: PaymentStatus; observations: string };
  onLicenseChange: (form: typeof licenseForm) => void;
  onPaymentChange: (form: typeof paymentForm) => void;
  onRenew: (event: FormEvent) => void;
  onTogglePayment: (subscriptionId: string | null) => void;
  onPaymentSubmit: (event: FormEvent, subscription: BranchSubscription) => void;
  onPaymentStatus: (payment: Payment, status: PaymentStatus) => void;
  onEdit: () => void;
}) {
  return (
    <aside className="card branch-detail">
      <div className="flex-between"><h2>{branch.name}</h2><button type="button" className="btn btn-secondary btn-sm" onClick={onEdit}>Editar</button></div>
      <p><strong>Empresa:</strong> {branch.company.commercialName}</p>
      <p><strong>Dirección:</strong> {[branch.address, branch.city, branch.department, branch.country].filter(Boolean).join(', ') || '—'}</p>
      <p><strong>Responsable:</strong> {branch.managerName || '—'}{branch.managerTitle ? ` · ${branch.managerTitle}` : ''}</p>
      <p><strong>Contacto:</strong> {branch.managerPhone || '—'} · {branch.managerEmail || '—'}</p>
      <h3>Administradores y empleados</h3>
      <ul className="platform-user-list">
        {(branch.users ?? []).map((branchUser) => <li key={branchUser.id}>{branchUser.fullName} · {branchUser.role === 'ADMIN' ? 'Administrador' : 'Empleado'} · {branchUser.isActive ? 'Activo' : 'Inactivo'}</li>)}
        {!branch.users?.length && <li>No hay usuarios cargados.</li>}
      </ul>

      <h3>Licencias e historial de pagos</h3>
      {(branch.subscriptions ?? []).map((subscription) => (
        <article className="subscription-card" key={subscription.id}>
          <strong>{periodLabels[subscription.period]} · {dateText(subscription.startDate)} a {dateText(subscription.endDate)}</strong>
          <div>Valor contratado: {moneyText(subscription.contractedAmount)}</div>
          {subscription.notes && <div className="text-muted"><strong>Nota:</strong> {subscription.notes}</div>}
          <button className="btn btn-secondary btn-sm" type="button" onClick={() => onTogglePayment(paymentSubscription === subscription.id ? null : subscription.id)}>
            Registrar pago
          </button>
          {paymentSubscription === subscription.id && (
            <form className="platform-form" onSubmit={(event) => onPaymentSubmit(event, subscription)}>
              <TextField label="Fecha de pago" type="date" value={paymentForm.paymentDate} onChange={(value) => onPaymentChange({ ...paymentForm, paymentDate: value })} required />
              <TextField label="Cantidad pagada" type="number" value={paymentForm.amountPaid} onChange={(value) => onPaymentChange({ ...paymentForm, amountPaid: value })} required />
              <TextField label="Medio de pago" value={paymentForm.paymentMethod} onChange={(value) => onPaymentChange({ ...paymentForm, paymentMethod: value })} required />
              <div className="field"><label htmlFor={`payment-status-${subscription.id}`}>Estado</label>
                <select id={`payment-status-${subscription.id}`} value={paymentForm.status} onChange={(event) => onPaymentChange({ ...paymentForm, status: event.target.value as PaymentStatus })}>
                  <option value="PENDIENTE">Pendiente</option><option value="PARCIAL">Parcial</option><option value="PAGADO">Pagado</option><option value="ANULADO">Anulado</option>
                </select>
              </div>
              <TextField label="Observaciones" value={paymentForm.observations} onChange={(value) => onPaymentChange({ ...paymentForm, observations: value })} />
              <button className="btn btn-primary" type="submit" disabled={saving}>Guardar pago</button>
            </form>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Fecha</th><th>Medio</th><th>Recibido</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                {(subscription.payments ?? []).map((payment) => (
                  <tr key={payment.id}>
                    <td>{dateText(payment.paymentDate)}</td><td>{payment.paymentMethod}</td><td>{moneyText(payment.amountPaid)}</td>
                    <td>{payment.status}</td><td>
                      {payment.status !== 'ANULADO' && <button type="button" className="btn-link" onClick={() => onPaymentStatus(payment, 'ANULADO')}>Anular</button>}
                    </td>
                  </tr>
                ))}
                {!subscription.payments?.length && <tr><td colSpan={5}>Sin pagos registrados.</td></tr>}
              </tbody>
            </table>
          </div>
        </article>
      ))}

      <h3>Renovar / asignar licencia</h3>
      <p className="hint">El período se inicializa con la última licencia de esta sede. Registrar una licencia crea un nuevo registro y no reemplaza las anteriores.</p>
      <form className="platform-form" onSubmit={onRenew}>
        <PeriodField value={licenseForm.period} onChange={(period) => onLicenseChange({ ...licenseForm, period })} />
        <TextField label="Fecha de inicio" type="date" value={licenseForm.startDate} onChange={(value) => onLicenseChange({ ...licenseForm, startDate: value })} required />
        <TextField label="Valor contratado" type="number" value={licenseForm.contractedAmount} onChange={(value) => onLicenseChange({ ...licenseForm, contractedAmount: value })} required />
        <button className="btn btn-primary" type="submit" disabled={saving}>Registrar licencia</button>
      </form>
    </aside>
  );
}
